import React, { useEffect, useState } from 'react';
import { api, auth, imageUrl, PROBLEM_AREAS, STATUSES } from '../api/client';
import { StatusBadge, PriorityBadge, Spinner } from './Shared';
import { humanSize } from '../api/images';

export default function TicketDetail({ id, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isAdmin = auth.isAdmin;

  // admin controls
  const [newStatus, setNewStatus] = useState(null);
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function load() {
    setLoading(true); setError('');
    try {
      const t = await api.getTicket(id);
      setTicket(t);
      setNewStatus(STATUSES.find((s) => s.key === t.status)?.value ?? 0);
      setRemark(t.remark || '');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function saveAdmin() {
    setSaving(true); setSaved(false); setError('');
    try {
      const updated = await api.updateStatus(id, newStatus, remark);
      setTicket(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="screen"><Spinner big /></div>;
  if (!ticket) return <div className="screen"><p className="error-text">{error || 'Not found'}</p></div>;

  return (
    <div className="screen">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
        <h1 style={{ fontSize: 20 }}>Ticket #{ticket.id}</h1>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <h2>{ticket.title}</h2>
          <StatusBadge status={ticket.status} />
        </div>
        <div className="ticket-meta" style={{ marginTop: 10 }}>
          <span className="ticket-area">{areaLabel(ticket.problemArea)}</span>
          <PriorityBadge priority={ticket.priority} />
        </div>
        {ticket.description && (
          <>
            <div className="divider" />
            <p className="muted" style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
          </>
        )}
      </div>

      {ticket.images?.length > 0 && (
        <div className="card">
          <label className="label">Photos</label>
          <div className="thumbs">
            {ticket.images.map((img) => (
              <a className="thumb" key={img.id} href={imageUrl(img.url)} target="_blank" rel="noreferrer">
                <img src={imageUrl(img.url)} alt="" />
                <span className="sz">{humanSize(img.sizeBytes)}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="kv"><span className="k">Raised by</span><span className="v">{ticket.userName || ticket.userMobile}</span></div>
        {isAdmin && <div className="kv"><span className="k">Mobile</span><span className="v">{ticket.userMobile}</span></div>}
        <div className="kv"><span className="k">Company</span><span className="v">{ticket.companyName || '—'}</span></div>
        <div className="kv"><span className="k">Location</span><span className="v">{ticket.clientLocation || '—'}</span></div>
        <div className="kv"><span className="k">Created</span><span className="v">{fmtDateTime(ticket.createdAt)}</span></div>
        <div className="kv"><span className="k">Updated</span><span className="v">{fmtDateTime(ticket.updatedAt)}</span></div>
      </div>

      {/* Remark — visible to everyone if present; editable by admin below */}
      {!isAdmin && ticket.remark && (
        <div className="card" style={{ background: 'var(--sky-50)', borderColor: 'var(--sky-200)' }}>
          <label className="label">Remark from team</label>
          <p className="muted" style={{ whiteSpace: 'pre-wrap' }}>{ticket.remark}</p>
        </div>
      )}

      {isAdmin && (
        <div className="card">
          <h2 style={{ marginBottom: 12 }}>Admin · update</h2>
          <div className="field">
            <label className="label">Status</label>
            <div className="seg">
              {STATUSES.map((s) => (
                <button key={s.value} type="button"
                  className={`seg-btn ${newStatus === s.value ? 'active' : ''}`}
                  onClick={() => setNewStatus(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <label className="label">Remark</label>
            <textarea className="textarea" placeholder="Add a note for the client…"
              value={remark} onChange={(e) => setRemark(e.target.value)} maxLength={2000} />
          </div>
          <button className="btn btn-primary" disabled={saving} onClick={saveAdmin}>
            {saving ? <span className="spinner" /> : 'Save changes'}
          </button>
          {saved && <p className="ok-text">Saved ✓</p>}
          {error && <p className="error-text">{error}</p>}
        </div>
      )}
    </div>
  );
}

function areaLabel(key) {
  const f = PROBLEM_AREAS.find((a) => a.key === key);
  return f ? f.label : key;
}
function fmtDateTime(iso) {
  try { return new Date(iso).toLocaleString(undefined, { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }); }
  catch { return ''; }
}
